import React, { useState, useRef, useCallback, useEffect } from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { ChevronRight, Check } from "@mui/icons-material";

/**
 * SlideToConfirm - A slide-to-confirm control for critical actions
 *
 * Used for destructive or irreversible actions where we want the user
 * to be very deliberate about their confirmation.
 *
 * Test/a11y handles (all optional, defaulted so every call site gets one):
 *   testId    - data-testid on the track (the element a drag is measured against)
 *   ariaLabel - accessible name; defaults to the visible `label`
 * The track also mirrors `disabled` / `processing` / `label` onto attributes so
 * a spec can assert the gate state without reading pixels.
 */
const SlideToConfirm = ({
  onConfirm,
  disabled = false,
  processing = false,
  label = "Slide to Confirm",
  processingLabel = "Processing...",
  width = 320,
  height = 56,
  color = "warning", // "warning", "error", "success", "primary"
  testId = "slide-to-confirm",
  ariaLabel,
}) => {
  const theme = useTheme();
  const trackRef = useRef(null);
  const [dragPosition, setDragPosition] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);

  const thumbSize = height - 8;
  const maxDrag = width - thumbSize - 8;
  const confirmThreshold = maxDrag * 0.85;

  // Get colors based on color prop
  const getColors = () => {
    const colorMap = {
      warning: {
        track: theme.palette.grey[200],
        fill: theme.palette.warning.light,
        thumb: theme.palette.warning.main,
        border: theme.palette.warning.main,
      },
      error: {
        track: theme.palette.grey[200],
        fill: theme.palette.error.light,
        thumb: theme.palette.error.main,
        border: theme.palette.error.main,
      },
      success: {
        track: theme.palette.grey[200],
        fill: theme.palette.success.light,
        thumb: theme.palette.success.main,
        border: theme.palette.success.main,
      },
      primary: {
        track: theme.palette.grey[200],
        fill: theme.palette.primary.light,
        thumb: theme.palette.primary.main,
        border: theme.palette.primary.main,
      },
    };
    return colorMap[color] || colorMap.warning;
  };

  const colors = getColors();

  const handleDragStart = useCallback(
    (e) => {
      if (disabled || processing || isCompleted) return;
      e.preventDefault();
      setIsDragging(true);
    },
    [disabled, processing, isCompleted],
  );

  const handleDragMove = useCallback(
    (e) => {
      if (!isDragging || disabled || processing || isCompleted) return;

      const track = trackRef.current;
      if (!track) return;

      const trackRect = track.getBoundingClientRect();
      const clientX = e.type.includes("touch")
        ? e.touches[0].clientX
        : e.clientX;
      const relativeX = clientX - trackRect.left - thumbSize / 2 - 4;
      const newPosition = Math.max(0, Math.min(relativeX, maxDrag));

      setDragPosition(newPosition);
    },
    [isDragging, disabled, processing, isCompleted, thumbSize, maxDrag],
  );

  // Unlock the gate. Was previously defined and never called from anywhere, so
  // a slider that completed stayed completed forever: if the action behind it
  // FAILED or was refused, the control sat in its finished position with no way
  // back short of a page reload. Most visible on a refused reconciliation write,
  // where the user now gets an error message AND a dead control.
  const reset = useCallback(() => {
    setIsCompleted(false);
    setDragPosition(0);
  }, []);

  // Live mirrors so the post-confirm check reads current values rather than the
  // ones captured when the drag ended.
  const mountedRef = useRef(true);
  const processingRef = useRef(processing);
  processingRef.current = processing;
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const handleDragEnd = useCallback(() => {
    if (!isDragging) return;
    setIsDragging(false);

    if (dragPosition >= confirmThreshold && !disabled && !processing) {
      setIsCompleted(true);
      setDragPosition(maxDrag);

      // Signal 1 — the action settled without the parent taking over.
      // Covers a synchronous refusal (a guard that returns having written
      // nothing) and an async handler that rejects. If the parent instead
      // starts work, `processing` is already true by now and signal 2 owns the
      // reset. If the parent unmounts us on success, neither fires.
      const settle = () => {
        if (mountedRef.current && !processingRef.current) reset();
      };
      let result;
      try {
        result = onConfirm?.();
      } finally {
        Promise.resolve(result).then(settle, settle);
      }
    } else {
      setDragPosition(0);
    }
  }, [
    isDragging,
    dragPosition,
    confirmThreshold,
    disabled,
    processing,
    maxDrag,
    onConfirm,
    reset,
  ]);

  // Signal 2 — the parent finished working and we are still here, which means
  // the action did not take us away: it failed, or it was rejected. Unlock so
  // the user can act on whatever the error told them.
  const wasProcessingRef = useRef(processing);
  useEffect(() => {
    if (wasProcessingRef.current && !processing) reset();
    wasProcessingRef.current = processing;
  }, [processing, reset]);

  const progress = dragPosition / maxDrag;

  return (
    <Box
      ref={trackRef}
      data-testid={testId}
      role="button"
      aria-label={ariaLabel || label}
      aria-disabled={disabled ? "true" : "false"}
      aria-busy={processing ? "true" : "false"}
      data-disabled={disabled ? "true" : "false"}
      data-processing={processing ? "true" : "false"}
      data-confirm-label={label}
      onMouseMove={handleDragMove}
      onMouseUp={handleDragEnd}
      onMouseLeave={handleDragEnd}
      onTouchMove={handleDragMove}
      onTouchEnd={handleDragEnd}
      sx={{
        position: "relative",
        width: width,
        height: height,
        borderRadius: height / 2,
        backgroundColor: isCompleted
          ? theme.palette.success.main
          : colors.track,
        overflow: "hidden",
        cursor: disabled || processing ? "not-allowed" : "default",
        opacity: disabled ? 0.5 : 1,
        transition: isCompleted
          ? "background-color 0.3s ease"
          : "background-color 0.15s ease",
        userSelect: "none",
        touchAction: "none",
        border: `2px solid ${
          isCompleted
            ? theme.palette.success.dark
            : isDragging
              ? colors.border
              : theme.palette.grey[400]
        }`,
      }}>
      {/* Progress fill */}
      <Box
        sx={{
          position: "absolute",
          left: 0,
          top: 0,
          height: "100%",
          width: `${progress * 100}%`,
          backgroundColor: isCompleted
            ? theme.palette.success.main
            : colors.fill,
          transition: isDragging ? "none" : "all 0.3s ease",
          borderRadius: height / 2,
        }}
      />

      {/* Label text */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          pointerEvents: "none",
        }}>
        <Typography
          variant="body2"
          sx={{
            color: isCompleted
              ? theme.palette.success.contrastText
              : theme.palette.text.secondary,
            fontWeight: 600,
            fontSize: "0.85rem",
            letterSpacing: "0.02em",
            transition: "color 0.3s ease, opacity 0.3s ease",
            opacity: progress > 0.3 ? 0 : 1,
            pl: 6,
          }}>
          {processing ? processingLabel : label}
        </Typography>
      </Box>

      {/* Animated chevrons (hint arrows) */}
      {!isDragging && !isCompleted && !processing && (
        <Box
          sx={{
            position: "absolute",
            left: thumbSize + 16,
            top: "50%",
            transform: "translateY(-50%)",
            display: "flex",
            alignItems: "center",
            gap: 0,
            pointerEvents: "none",
            animation: "slideHint 1.5s ease-in-out infinite",
            "@keyframes slideHint": {
              "0%, 100%": { opacity: 0.3, transform: "translateY(-50%)" },
              "50%": {
                opacity: 0.7,
                transform: "translateY(-50%) translateX(8px)",
              },
            },
          }}>
          <ChevronRight
            sx={{ color: theme.palette.grey[500], fontSize: 20, ml: -1 }}
          />
          <ChevronRight
            sx={{ color: theme.palette.grey[400], fontSize: 20, ml: -2 }}
          />
          <ChevronRight
            sx={{ color: theme.palette.grey[300], fontSize: 20, ml: -2 }}
          />
        </Box>
      )}

      {/* Draggable thumb */}
      <Box
        onMouseDown={handleDragStart}
        onTouchStart={handleDragStart}
        sx={{
          position: "absolute",
          left: 4 + dragPosition,
          top: "50%",
          transform: "translateY(-50%)",
          width: thumbSize,
          height: thumbSize,
          borderRadius: "50%",
          backgroundColor: isCompleted
            ? theme.palette.success.dark
            : colors.thumb,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: disabled || processing ? "not-allowed" : "grab",
          transition: isDragging ? "none" : "all 0.3s ease",
          boxShadow: isDragging
            ? `0 4px 12px ${colors.thumb}40`
            : `0 2px 8px rgba(0,0,0,0.2)`,
          "&:active": {
            cursor: disabled || processing ? "not-allowed" : "grabbing",
          },
          "&:hover": {
            transform: !disabled && !processing && !isDragging
              ? "translateY(-50%) scale(1.05)"
              : "translateY(-50%)",
          },
        }}>
        {processing ? (
          <CircularProgress size={24} sx={{ color: "white" }} />
        ) : isCompleted ? (
          <Check sx={{ color: "white", fontSize: 28 }} />
        ) : (
          <ChevronRight sx={{ color: "white", fontSize: 28 }} />
        )}
      </Box>
    </Box>
  );
};

export default SlideToConfirm;
